import {
  Avatar,
  Checkbox,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Dispatch, SetStateAction } from 'react';
import { FunctionComponent } from 'react';
import './styles/SettingsDrawer.scss';
import { SelectedEventsModel } from '../types';
import StudiesIcon from 'assets/study.svg';
import EventIcon from 'assets/event.svg';
import GeneralIcon from 'assets/general.svg';
import LegalIcon from 'assets/legal.svg';
import HolidayIcon from 'assets/holiday.svg';
import { translations } from 'locales/i18n';
import { useTranslation } from 'react-i18next';

interface SettingsDrawerProps {
  toggleDrawer: (open: boolean) => void;
  open: boolean;
  setShowArticles: Dispatch<SetStateAction<SelectedEventsModel | undefined>>;
  showArticles: SelectedEventsModel | undefined;
}

export const SettingsDrawer: FunctionComponent<SettingsDrawerProps> = ({
  toggleDrawer,
  open,
  setShowArticles,
  showArticles
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  const mapKeyToData = (
    name: string
  ): { icon: string; name: string; disabled?: boolean } | null => {
    switch (name) {
      case 'legal':
        return {
          icon: LegalIcon,
          name: `${t(translations.pages.calendar.type.legal())}`,
          disabled: true
        };
      case 'general':
        return {
          icon: GeneralIcon,
          name: `${t(translations.pages.calendar.type.general())}`
        };
      case 'event':
        return {
          icon: EventIcon,
          name: `${t(translations.pages.calendar.type.event())}`
        };
      case 'study':
        return {
          icon: StudiesIcon,
          name: `${t(translations.pages.calendar.type.study())}`
        };
      case 'holiday':
        return {
          icon: HolidayIcon,
          name: `${t(translations.pages.calendar.type.holiday())}`
        };
      default:
        return null;
    }
  };

  const handleToggle = (value: string) => {
    const newChecked: SelectedEventsModel = {
      ...(showArticles as SelectedEventsModel)
    };
    newChecked[value] = !newChecked[value];

    setShowArticles(newChecked);
  };

  const list = () =>
    showArticles && (
      <div className="calendar-settings__drawer__menu" role="presentation">
        <List dense>
          {smallAndDown && (
            <IconButton
              className="calendar-settings__drawer__back-button"
              onClick={() => toggleDrawer(false)}
            >
              <Close fontSize="large" />
            </IconButton>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '50px',
              width: '100%'
            }}
          >
            <Typography variant="h5" component="h2">
              {t(translations.pages.calendar.settings())}
            </Typography>
          </div>
          <Divider style={{ margin: '1em 1em 2em 1em' }} />
          {Object.entries(showArticles).map(([key, value]) => {
            const labelId = `checkbox-list-secondary-label-${key}`;
            const data = mapKeyToData(key);
            if (data == null) {
              return null;
            }
            return (
              <ListItem key={key}>
                <ListItemAvatar>
                  <Avatar alt={`${key}`} src={data.icon} />
                </ListItemAvatar>
                <ListItemText id={labelId} primary={data.name} />
                <ListItemSecondaryAction>
                  <Checkbox
                    disabled={data.disabled}
                    edge="end"
                    onChange={() => handleToggle(key)}
                    checked={value}
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      </div>
    );
  return (
    <Drawer
      className="calendar-settings__drawer"
      PaperProps={{ style: { zIndex: 1 } }}
      anchor="right"
      open={open}
      onClose={() => toggleDrawer(false)}
      variant="persistent"
    >
      {list()}
    </Drawer>
  );
};
